/**
 * SHXRE Videos – home tab content: vertical scroll of published Studio videos
 * (same style as Studio Feed: video player, title, description, view count).
 */

import {useCallback, useState} from 'react'
import {View} from 'react-native'
import {msg, Trans} from '@lingui/macro'
import {useLingui} from '@lingui/react'

import {useHeaderOffset} from '#/components/hooks/useHeaderOffset'
import {useInitialNumToRender} from '#/lib/hooks/useInitialNumToRender'
import {cleanError} from '#/lib/strings/errors'
import {useStudioPublishedVideosQuery} from '#/state/queries/studio'
import {List, type ListRef} from '#/view/com/util/List'
import {useAnimatedRef} from 'react-native-reanimated'
import {atoms as a, useTheme} from '#/alf'
import {Button, ButtonIcon, ButtonText} from '#/components/Button'
import {ArrowRotateCounterClockwise_Stroke2_Corner0_Rounded as RetryIcon} from '#/components/icons/ArrowRotate'
import {VideoClip_Stroke2_Corner0_Rounded as VideoIcon} from '#/components/icons/VideoClip'
import {Text} from '#/components/Typography'
import type {StudioVideo} from '#/lib/api/studio'
import {StudioFeedVideoCard} from '#/screens/Studio/StudioFeedVideoCard'

function renderFeedItem({item}: {item: StudioVideo}) {
  return <StudioFeedVideoCard video={item} />
}

function keyExtractor(item: StudioVideo) {
  return item.id
}

export function ShxreVideosFeedPage() {
  const {_} = useLingui()
  const t = useTheme()
  const headerOffset = useHeaderOffset()
  const initialNumToRender = useInitialNumToRender({minItemHeight: 200})
  const scrollRef: ListRef = useAnimatedRef()
  const [isPTRing, setIsPTRing] = useState(false)

  const {
    data: videos = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useStudioPublishedVideosQuery()

  const onRefresh = useCallback(async () => {
    setIsPTRing(true)
    try {
      await refetch()
    } finally {
      setIsPTRing(false)
    }
  }, [refetch])

  if (videos.length === 0 && !isLoading) {
    return (
      <View style={[a.flex_1, a.justify_center]}>
        {isError ? (
          <View style={[a.pt_3xl, a.align_center]}>
            <VideoIcon width={48} fill={t.atoms.text_contrast_low.color} />
            <Text
              style={[
                a.pt_md,
                a.pb_sm,
                a.text_2xl,
                a.font_semi_bold,
                t.atoms.text,
              ]}>
              <Trans>Whoops!</Trans>
            </Text>
            <Text
              style={[
                a.text_md,
                a.pb_xl,
                a.text_center,
                t.atoms.text_contrast_medium,
                {maxWidth: 360},
              ]}>
              {cleanError(error) || _(msg`Failed to load videos`)}
            </Text>
            <Button
              label={_(msg`Retry`)}
              size="small"
              color="secondary_inverted"
              variant="solid"
              onPress={() => refetch()}>
              <ButtonText>
                <Trans>Retry</Trans>
              </ButtonText>
              <ButtonIcon icon={RetryIcon} position="right" />
            </Button>
          </View>
        ) : (
          <View style={[a.pt_3xl, a.align_center]}>
            <VideoIcon width={48} fill={t.palette.primary_500} />
            <Text
              style={[
                a.pt_md,
                a.pb_sm,
                a.text_2xl,
                a.font_semi_bold,
                t.atoms.text,
              ]}>
              <Trans>No videos yet</Trans>
            </Text>
            <Text
              style={[
                a.text_md,
                a.pb_xl,
                a.text_center,
                t.atoms.text_contrast_medium,
              ]}>
              <Trans>Published SHXRE videos will appear here.</Trans>
            </Text>
          </View>
        )}
      </View>
    )
  }

  return (
    <List
      ref={scrollRef}
      data={videos}
      renderItem={renderFeedItem}
      keyExtractor={keyExtractor}
      refreshing={isPTRing}
      onRefresh={onRefresh}
      onEndReachedThreshold={1.5}
      initialNumToRender={initialNumToRender}
      windowSize={11}
      desktopFixedHeight
      sideBorders={false}
      headerOffset={headerOffset}
    />
  )
}
